import React, { useContext, type JSX } from 'react';
import { useMutation } from '@apollo/client';
import SaveMutation from './SaveAnalysMutation.gql';
import {
  SaveAnalysVariables as ISaveVariables,
  SaveAnalys as ISaveData,
} from './_QueryTypes/SaveAnalys';
import EkanalysQuery from '../../../../../../Queries/EkonomiskAnalysSettingsQuery/EkAnalys.gql';
import AnalysListQuery from '../../../../../../Queries/EkonomiskAnalysListQuery/AnalysListQuery.gql';
import AnalysEgnaLokalaQuery from '../../Queries/AnalysEgnaLokalaQuery.gql';
import { MutationFunction as MutationFn } from '@apollo/client';
import { EkonomiskAnalysContextConsumer } from '../../../../../Context/EkonomiskAnalysContext';
import AddToRecentAnalyserMutation, {
  AddToRecentAnalyserFnType,
} from '../../../../EkonomiskAnalysSelect/AddToRecentAnalyserMutation/AddToRecentAnalyserMutation';
import {
  ISelectedAnalys,
  IGrundurval,
} from '../../../../../../EkonomiskAnalysPage';
import { IUserInfo } from '../../../../../../Utils/ekonomiskAnalysInitQueryUtils';
import { ActiveDatabaseContext } from '../../../../../../../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';

export type SaveAnalysCallbackType = (
  id: string,
  analysName: string,
  utdelningLopnr: number | null,
  grundurval: IGrundurval | undefined
) => void;

export type SaveAnalysFnType = (
  mutationFn: ISaveVariables,
  selectedAnalys: ISelectedAnalys,
  grundurval: IGrundurval | undefined,
  userInfo?: IUserInfo,
  callback?: SaveAnalysCallbackType
) => void;

interface IProps {
  children: (saveAnalys: SaveAnalysFnType) => JSX.Element;
}

type saveAnalysMutationFnType = MutationFn<ISaveData, ISaveVariables>;

const SaveAnalysMutation: React.FunctionComponent<IProps> = props => {
  const saveAnalys =
    (
      mutateFn: saveAnalysMutationFnType,
      addToRecentAnalyser: AddToRecentAnalyserFnType,
      codeSystemId: string,
      activeDatabase: string | null
    ): SaveAnalysFnType =>
    (variables, selectedAnalys, grundurval, userInfo?, callback?) => {
      const refetchQueries = variables.analys.id
        ? [
            {
              query: EkanalysQuery,
              variables: { id: variables.analys.id },
            },
            {
              query: AnalysListQuery,
              variables: {
                codeSystemId,
                onlyFavorites: false,
                dbName: activeDatabase ?? '',
              },
            },
          ]
        : [];
      mutateFn({
        variables,
        refetchQueries: [
          ...refetchQueries,
          {
            query: AnalysEgnaLokalaQuery,
            variables: { dbname: variables.dbname, endast_anvandarens: true },
          },
        ],
        awaitRefetchQueries: true,
      }).then(res => {
        if (res && res.data) {
          addToRecentAnalyser(
            {
              id: res.data.ekanalys_spara.id,
              name: res.data.ekanalys_spara.name || '',
              utdelningLopnr: selectedAnalys.analys.utdelningLopnr,
              grundurval: grundurval,
              detaljanalys: selectedAnalys.analys.detaljanalys,
              ai_prompt: selectedAnalys.analys.ai_prompt,
            },
            selectedAnalys.codePrivilege,
            userInfo && userInfo.recentAnalyser ? userInfo.recentAnalyser : []
          );
          if (callback) {
            const analysName = res.data.ekanalys_spara.name
              ? res.data.ekanalys_spara.name
              : '';
            callback(
              res.data.ekanalys_spara.id,
              analysName,
              selectedAnalys.analys.utdelningLopnr,
              grundurval
            );
          }
        }
      });
    };

  const [saveAnalysFn] = useMutation<ISaveData, ISaveVariables>(SaveMutation);
  const { activeDatabase } = useContext(ActiveDatabaseContext);

  return (
    <EkonomiskAnalysContextConsumer>
      {({ codeSystemId }) => (
        <AddToRecentAnalyserMutation recentAnalyser={[]}>
          {addToRecentAnalyser =>
            props.children(
              saveAnalys(
                saveAnalysFn,
                addToRecentAnalyser,
                codeSystemId,
                activeDatabase
              )
            )
          }
        </AddToRecentAnalyserMutation>
      )}
    </EkonomiskAnalysContextConsumer>
  );
};

export default SaveAnalysMutation;
